import client, { APIError } from '@/utils/api-client'

export type ReportCardImportedCourse = {
  courseId: string
  grade: number
  exam: number
  exam2: number
}

export type ReportCardImportRowType = 'course' | 'category' | 'special'

export type ReportCardImportMatch = {
  rowType: ReportCardImportRowType
  matchedCourseId: string
  matchedCourseName: string
  rawCourseName: string
  grade: number
  exam: number
  exam2: number
  confidence: number
  notes?: string | null
}

export type ReportCardImportUnmatched = {
  rowType: ReportCardImportRowType
  rawCourseName: string
  grade: number
  exam: number
  exam2: number
  notes: string
}

export type ReportCardImportResponse = {
  imports: ReportCardImportMatch[]
  unmatched: ReportCardImportUnmatched[]
  warnings: string[]
}

export type ReportCardCourseCatalogItem = {
  id: string
  name: string
}

const reportCardImportErrorMessages = {
  FILE_REQUIRED: 'Du må velge en fil først.',
  FILE_EMPTY: 'Filen er tom.',
  RATE_LIMITED: 'Det er for mange forsøk akkurat nå. Prøv igjen om litt.',
  UNSUPPORTED_FILE_TYPE:
    'Filtypen støttes ikke. Bruk PDF, JPG, PNG, WEBP eller HEIC.',
  COURSE_CATALOG_REQUIRED: 'Kunne ikke lese faglisten.',
  COURSE_CATALOG_INVALID_JSON: 'Kunne ikke lese faglisten.',
  INVALID_COURSE_CATALOG: 'Kunne ikke lese faglisten.',
  EMPTY_COURSE_CATALOG: 'Faglisten er tom.',
  INVALID_IMPORT_RESPONSE: 'Kunne ikke tolke svaret fra AI-tjenesten.',
  IMPORT_TIMEOUT: 'Analysen tok for lang tid. Prøv igjen.',
  IMPORT_FAILED: 'Kunne ikke lese dokumentet.',
} as const

function formatMegabytes(bytes: number) {
  return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`
}

function resolveImportErrorMessage(error: unknown) {
  if (!(error instanceof APIError) || !error.data || typeof error.data !== 'object') {
    return null
  }

  const errorCode = 'errorCode' in error.data ? error.data.errorCode : null

  if (typeof errorCode !== 'string') {
    return null
  }

  if (
    errorCode === 'FILE_TOO_LARGE' &&
    'maxBytes' in error.data &&
    typeof error.data.maxBytes === 'number'
  ) {
    return `Filen er for stor (maks ${formatMegabytes(error.data.maxBytes)}).`
  }

  return (
    reportCardImportErrorMessages[
      errorCode as keyof typeof reportCardImportErrorMessages
    ] || null
  )
}

export async function importReportCard(
  file: File,
  courseCatalog: ReportCardCourseCatalogItem[]
) {
  const formData = new FormData()
  formData.set('file', file)
  formData.set('courseCatalog', JSON.stringify(courseCatalog))

  try {
    return (await client('/ai-streamer/student-calculator/report-card-import', {
      method: 'POST',
      body: formData,
    })) as ReportCardImportResponse
  } catch (error) {
    const message = resolveImportErrorMessage(error)

    if (message) {
      throw new Error(message)
    }

    throw error
  }
}
